import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * Hides flex children that overflow their container's bounds.
 *
 * On each resize the hook resets all children to visible, measures
 * which ones fit, then sets `display: none` on the rest. Using
 * `display: none` (rather than `visibility: hidden`) ensures the
 * entire element tree is hidden — child components that set their
 * own visibility won't bleed through.
 *
 * The container must have `overflow: hidden` and `flex-wrap: nowrap`
 * so that the reset-then-measure cycle doesn't cause visual flicker
 * (ResizeObserver callbacks run before paint).
 */
export function useHideOverflowItems(
  containerRef: RefObject<HTMLDivElement | null>
) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const update = () => {
      const children = Array.from(container.children) as HTMLElement[];

      // Reset all children so we can measure their natural positions.
      for (const child of children) {
        child.style.display = '';
      }

      const containerRight = Math.floor(
        container.getBoundingClientRect().right
      );
      let overflowing = false;

      for (const child of children) {
        if (overflowing) {
          child.style.display = 'none';
          continue;
        }

        const childRight = Math.ceil(child.getBoundingClientRect().right);
        if (childRight > containerRight) {
          child.style.display = 'none';
          overflowing = true;
        }
      }
    };

    const observer = new ResizeObserver(update);
    observer.observe(container);

    return () => observer.disconnect();
  }, [containerRef]);
}
